import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { dashboardService } from "../services/dashboard.service";
import {
  buildAssignedMetricsDetailPayload,
  buildScopePayload,
  DEFAULT_CATEGORY_SUMMARY,
  DEFAULT_LIST_DATA,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SUMMARY,
  getAssignedMetricsGroupKey,
  SEARCH_DEBOUNCE_MS,
} from "../utils/assigned-metrics-drawer";
import { resolveSelectedReportingPeriod } from "../utils/reporting-years";

const useDebouncedValue = (value, delayMs) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => window.clearTimeout(timeoutId);
  }, [delayMs, value]);

  return debouncedValue;
};

const buildDetailPageKey = (page, pageSize) => `${page}:${pageSize}`;

const normalizeDetailPagination = (detailState, overrides = {}) => {
  const page = Number(overrides.page ?? detailState?.page ?? 1);
  const pageSize = Number(
    overrides.pageSize ?? detailState?.pageSize ?? DEFAULT_PAGE_SIZE,
  );

  return {
    page: Number.isNaN(page) || page < 1 ? 1 : page,
    pageSize:
      Number.isNaN(pageSize) || pageSize < 1 ? DEFAULT_PAGE_SIZE : pageSize,
  };
};

export const useAssignedMetricsDrawerData = ({
  open,
  filters,
  available,
  t,
}) => {
  const scopeYears = filters.years;
  const scopeFrameworks = filters.frameworks;
  const scopeCategories = filters.categories;
  const scopeOrgIds = filters.orgIds;
  const orgTree = available.orgTree;
  const reportingYears = available.reportingYears;
  const hierarchy = filters.hierarchy;

  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [categorySummary, setCategorySummary] = useState(
    DEFAULT_CATEGORY_SUMMARY,
  );
  const [listData, setListData] = useState(DEFAULT_LIST_DATA);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [listError, setListError] = useState("");
  const [summaryReloadToken, setSummaryReloadToken] = useState(0);
  const [categoryReloadToken, setCategoryReloadToken] = useState(0);
  const [listReloadToken, setListReloadToken] = useState(0);
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState("metric");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [openGroups, setOpenGroups] = useState({});
  const [detailCache, setDetailCache] = useState({});

  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const detailScopeKeyRef = useRef("");
  const detailRequestVersionRef = useRef({});
  const openGroupsRef = useRef({});
  const detailCacheRef = useRef({});
  const listResetKeyRef = useRef("");
  const reportingPeriod = useMemo(
    () => resolveSelectedReportingPeriod(scopeYears, reportingYears),
    [reportingYears, scopeYears],
  );

  const scopePayload = useMemo(
    () =>
      buildScopePayload(
        {
          years: scopeYears || [],
          frameworks: scopeFrameworks || [],
          categories: scopeCategories || [],
          hierarchy,
          orgIds: scopeOrgIds || [],
          ...(reportingPeriod || {}),
        },
        orgTree || [],
      ),
    [
      hierarchy,
      orgTree,
      reportingPeriod,
      scopeCategories,
      scopeFrameworks,
      scopeOrgIds,
      scopeYears,
    ],
  );

  const shouldWaitForOrgTree =
    open && (scopeOrgIds?.length || 0) > 0 && (orgTree?.length || 0) === 0;

  const queryPayload = useMemo(
    () => ({
      ...scopePayload,
      search: debouncedSearch,
      groupBy,
    }),
    [debouncedSearch, groupBy, scopePayload],
  );

  const listPayload = useMemo(
    () => ({
      ...queryPayload,
      groupBy,
      page,
      pageSize,
    }),
    [groupBy, page, pageSize, queryPayload],
  );

  const listResetKey = useMemo(
    () =>
      JSON.stringify({
        ...queryPayload,
      }),
    [queryPayload],
  );

  const detailScopeKey = useMemo(
    () =>
      JSON.stringify({
        ...queryPayload,
      }),
    [queryPayload],
  );

  const resetListing = useCallback(() => {
    startTransition(() => {
      setPage(1);
      setOpenGroups({});
      setDetailCache({});
    });
  }, []);

  const resetLocalState = useCallback(() => {
    setSearch("");
    setGroupBy("metric");
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setOpenGroups({});
    setDetailCache({});
  }, []);

  useEffect(() => {
    if (!open) return;

    resetLocalState();
    setSummary(DEFAULT_SUMMARY);
    setCategorySummary(DEFAULT_CATEGORY_SUMMARY);
    setListData(DEFAULT_LIST_DATA);
    setSummaryError("");
    setCategoryError("");
    setListError("");
  }, [open, resetLocalState]);

  useEffect(() => {
    detailScopeKeyRef.current = detailScopeKey;
  }, [detailScopeKey]);

  useEffect(() => {
    openGroupsRef.current = openGroups;
  }, [openGroups]);

  useEffect(() => {
    detailCacheRef.current = detailCache;
  }, [detailCache]);

  useEffect(() => {
    if (!open) return;

    detailRequestVersionRef.current = {};
    setDetailCache({});
    setOpenGroups({});
  }, [detailScopeKey, open]);

  useEffect(() => {
    if (!open || shouldWaitForOrgTree) return undefined;

    let cancelled = false;
    setSummaryLoading(true);
    setSummaryError("");

    dashboardService
      .getAssignedMetricsSummary(queryPayload)
      .then((response) => {
        if (cancelled) return;
        setSummary({
          uniqueMetrics: response?.uniqueMetrics || 0,
          assignments: response?.assignments || 0,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setSummary(DEFAULT_SUMMARY);
        setSummaryError(
          error?.message ||
            t("Unable to load assigned metrics summary for the current scope."),
        );
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, queryPayload, shouldWaitForOrgTree, summaryReloadToken, t]);

  useEffect(() => {
    if (!open || shouldWaitForOrgTree) return undefined;

    let cancelled = false;
    setCategoryLoading(true);
    setCategoryError("");

    dashboardService
      .getAssignedMetricsCategorySummary(queryPayload)
      .then((response) => {
        if (cancelled) return;
        setCategorySummary(
          Array.isArray(response?.categories)
            ? response.categories
            : DEFAULT_CATEGORY_SUMMARY,
        );
      })
      .catch((error) => {
        if (cancelled) return;
        setCategorySummary(DEFAULT_CATEGORY_SUMMARY);
        setCategoryError(
          error?.message ||
            t(
              "Unable to load assigned metrics category summary for the current scope.",
            ),
        );
      })
      .finally(() => {
        if (!cancelled) setCategoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [categoryReloadToken, open, queryPayload, shouldWaitForOrgTree, t]);

  useEffect(() => {
    if (!open || shouldWaitForOrgTree) return undefined;

    let cancelled = false;
    const shouldResetListData = listResetKeyRef.current !== listResetKey;
    listResetKeyRef.current = listResetKey;

    if (shouldResetListData) {
      setListData(DEFAULT_LIST_DATA);
    }

    setListLoading(true);
    setListError("");

    dashboardService
      .getAssignedMetricsList(listPayload)
      .then((response) => {
        if (cancelled) return;
        setListData({
          rows: Array.isArray(response?.rows) ? response.rows : [],
          total: response?.total || 0,
          page: response?.page || 1,
          pageSize: response?.pageSize || listPayload.pageSize,
          filteredAssignments: response?.filteredAssignments || 0,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setListData(DEFAULT_LIST_DATA);
        setListError(
          error?.message ||
            t("Unable to load assigned metrics rows for the current scope."),
        );
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    listPayload,
    listReloadToken,
    listResetKey,
    open,
    shouldWaitForOrgTree,
    t,
  ]);

  const fetchGroupDetail = useCallback(
    (row, { force = false, page, pageSize } = {}) => {
      const groupKey = getAssignedMetricsGroupKey(row);
      if (!groupKey) return;
      const existing = detailCacheRef.current[groupKey];
      const pagination = normalizeDetailPagination(existing, {
        page,
        pageSize,
      });
      const detailPageKey = buildDetailPageKey(
        pagination.page,
        pagination.pageSize,
      );
      const cachedPage = existing?.pages?.[detailPageKey];

      if (!force && cachedPage) {
        setDetailCache((current) => {
          const currentState = current[groupKey] || {};
          const nextState = {
            ...currentState,
            items: cachedPage.items,
            loading: false,
            loaded: true,
            error: "",
            total: cachedPage.total,
            page: cachedPage.page,
            pageSize: cachedPage.pageSize,
            totalPages: cachedPage.totalPages,
          };
          const nextCache = {
            ...current,
            [groupKey]: nextState,
          };
          detailCacheRef.current = nextCache;
          return nextCache;
        });
        return;
      }

      if (
        !force &&
        existing?.loading &&
        existing?.page === pagination.page &&
        existing?.pageSize === pagination.pageSize
      ) {
        return;
      }

      let requestPayload;
      try {
        requestPayload = buildAssignedMetricsDetailPayload(
          queryPayload,
          groupBy,
          row,
          pagination,
        );
      } catch (error) {
        setDetailCache((current) => {
          const nextCache = {
            ...current,
            [groupKey]: {
              items: [],
              loading: false,
              loaded: false,
              error:
                error?.message ||
                t("Unable to load the assigned metrics detail."),
              total: 0,
              page: pagination.page,
              pageSize: pagination.pageSize,
              totalPages: 1,
              pages: existing?.pages || {},
            },
          };
          detailCacheRef.current = nextCache;
          return nextCache;
        });
        return;
      }

      setDetailCache((current) => {
        const nextCache = {
          ...current,
          [groupKey]: {
            items: force ? [] : cachedPage?.items || existing?.items || [],
            loading: true,
            loaded: false,
            error: "",
            total: existing?.total || 0,
            page: pagination.page,
            pageSize: pagination.pageSize,
            totalPages: existing?.totalPages || 1,
            pages: existing?.pages || {},
          },
        };
        detailCacheRef.current = nextCache;
        return nextCache;
      });

      const requestScopeKey = detailScopeKey;
      const nextRequestVersion =
        (detailRequestVersionRef.current[groupKey] || 0) + 1;
      detailRequestVersionRef.current[groupKey] = nextRequestVersion;

      dashboardService
        .getAssignedMetricsDetail(requestPayload)
        .then((response) => {
          const isStaleScope = detailScopeKeyRef.current !== requestScopeKey;
          const isStaleRequest =
            detailRequestVersionRef.current[groupKey] !== nextRequestVersion;

          if (isStaleScope || isStaleRequest) return;

          const responseGroupKey = response?.groupKey || groupKey;
          const responsePage = response?.page || pagination.page;
          const responsePageSize = response?.pageSize || pagination.pageSize;
          const responseTotal = response?.total || 0;
          const responseTotalPages = Math.max(
            1,
            Math.ceil(responseTotal / responsePageSize) || 1,
          );
          const responseItems = Array.isArray(response?.items)
            ? response.items
            : [];
          const responsePageKey = buildDetailPageKey(
            responsePage,
            responsePageSize,
          );
          const nextState = {
            items: responseItems,
            loading: false,
            loaded: true,
            error: "",
            total: responseTotal,
            page: responsePage,
            pageSize: responsePageSize,
            totalPages: responseTotalPages,
            pages: {
              ...(existing?.pages || {}),
              [responsePageKey]: {
                items: responseItems,
                total: responseTotal,
                page: responsePage,
                pageSize: responsePageSize,
                totalPages: responseTotalPages,
              },
            },
          };

          setDetailCache((current) => {
            const currentGroupState = current[groupKey] || {};
            const nextCache = {
              ...current,
              [groupKey]: {
                ...currentGroupState,
                ...nextState,
                pages: {
                  ...(currentGroupState.pages || {}),
                  ...nextState.pages,
                },
              },
              ...(responseGroupKey === groupKey
                ? {}
                : {
                    [responseGroupKey]: {
                      ...(current[responseGroupKey] || {}),
                      ...nextState,
                      pages: {
                        ...(current[responseGroupKey]?.pages || {}),
                        ...nextState.pages,
                      },
                    },
                  }),
            };
            detailCacheRef.current = nextCache;
            return nextCache;
          });
        })
        .catch((error) => {
          const isStaleScope = detailScopeKeyRef.current !== requestScopeKey;
          const isStaleRequest =
            detailRequestVersionRef.current[groupKey] !== nextRequestVersion;

          if (isStaleScope || isStaleRequest) return;

          setDetailCache((current) => {
            const nextCache = {
              ...current,
              [groupKey]: {
                items: [],
                loading: false,
                loaded: false,
                error:
                  error?.message ||
                  t("Unable to load the assigned metrics detail."),
                total: existing?.total || 0,
                page: pagination.page,
                pageSize: pagination.pageSize,
                totalPages: existing?.totalPages || 1,
                pages: existing?.pages || {},
              },
            };
            detailCacheRef.current = nextCache;
            return nextCache;
          });
        });
    },
    [detailScopeKey, groupBy, queryPayload, t],
  );

  const toggleGroup = useCallback(
    (row) => {
      const groupKey = getAssignedMetricsGroupKey(row);
      if (!groupKey) return;

      const isOpen = Boolean(openGroupsRef.current[groupKey]);
      setOpenGroups((current) => {
        const nextOpenGroups = {
          ...current,
          [groupKey]: !current[groupKey],
        };
        openGroupsRef.current = nextOpenGroups;
        return nextOpenGroups;
      });

      if (!isOpen) {
        fetchGroupDetail(row);
      }
    },
    [fetchGroupDetail],
  );

  const retryGroupDetail = useCallback(
    (row) => {
      fetchGroupDetail(row, { force: true });
    },
    [fetchGroupDetail],
  );

  const setGroupDetailPage = useCallback(
    (row, nextPage) => {
      fetchGroupDetail(row, { page: nextPage });
    },
    [fetchGroupDetail],
  );

  const setGroupDetailPageSize = useCallback(
    (row, nextPageSize) => {
      const pageSize = Number(nextPageSize);
      if (Number.isNaN(pageSize) || pageSize < 1) return;
      fetchGroupDetail(row, { page: 1, pageSize });
    },
    [fetchGroupDetail],
  );

  const setGroupByAndReset = useCallback(
    (value) => {
      setGroupBy(value);
      resetListing();
    },
    [resetListing],
  );

  const setSearchAndReset = useCallback(
    (value) => {
      setSearch(value);
      resetListing();
    },
    [resetListing],
  );

  const setPageSizeAndReset = useCallback((value) => {
    const nextSize = Number(value);
    if (Number.isNaN(nextSize)) return;

    setPageSize(nextSize);
    startTransition(() => {
      setPage(1);
      setOpenGroups({});
      setDetailCache({});
    });
  }, []);

  const resetView = useCallback(() => {
    startTransition(() => {
      setPage(1);
      setOpenGroups({});
      setDetailCache({});
    });
  }, []);

  const currentPage = listData.page || 1;
  const totalPages = Math.max(
    1,
    Math.ceil((listData.total || 0) / pageSize) || 1,
  );

  return {
    search,
    setSearch: setSearchAndReset,
    groupBy,
    setGroupBy: setGroupByAndReset,
    page: currentPage,
    setPage,
    pageSize,
    setPageSize: setPageSizeAndReset,
    totalPages,
    openGroups,
    toggleGroup,
    detailCache,
    retryGroupDetail,
    setGroupDetailPage,
    setGroupDetailPageSize,
    resetView,
    summary,
    summaryLoading,
    summaryError,
    retrySummary: () => setSummaryReloadToken((value) => value + 1),
    categorySummary,
    categoryLoading,
    categoryError,
    retryCategory: () => setCategoryReloadToken((value) => value + 1),
    listData,
    listLoading,
    listError,
    retryList: () => setListReloadToken((value) => value + 1),
  };
};
